import { type RefObject, useContext, useState } from 'react';
import { Route } from 'react-router-dom';
import { useMedia } from 'react-use';
import { useScrollToTopFixIOS } from '@hungryroot/environment';
import {
  AntiInflamitory,
  CalorieConscious,
  GutHealth,
  HighFiber,
  HighProtein,
  Immunity,
  LowCarb,
  LowCholesterol,
  LowFat,
  LowFodmap,
  LowSodium,
  LowSugar,
} from '@hungryroot/icons';

import { Heading } from '@hungryroot/ramen';
import { segmentAnalytics } from '@hungryroot/segment';
import { tagGroups, tags } from '@hungryroot/tags';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import { ControlTagSelect } from '../../components/ControlTagSelect/ControlTagSelect';
import { QuizQuestionScreen } from '../../components/QuizQuestionScreen/QuizQuestionScreen';
import { SectionLabel } from '../../components/SectionLabel/SectionLabel';
import { Toggle, ToggleVariant } from '../../components/Toggle/Toggle/Toggle';
import { ROUTE_MACRO_NUTRIENT_LOW_SODIUM } from '../../defs/routes';
import { QuizContext } from '../../quiz-context';
import { SODIUM_HARD_CONSTRAINT_OPT_OUT_LS_KEY } from '../../quiz-variables';

import {
  type QuizPreferenceItem,
  tagAnalytics,
} from '../../segment/quizAnalytics';
import styles from './MacroNutrient.module.css';

type lowSodiumRestriction = 'unselectedSoft' | 'soft' | 'hard' | 'none';

interface UseLowSodiumHardRestriction {
  status: lowSodiumRestriction;
  onOptionSelect: (status: lowSodiumRestriction) => void;
}

export const useLowSodiumHardRestriction = (): UseLowSodiumHardRestriction => {
  const { quiz, onQuestionAnswer } = useContext(QuizContext);
  const macroSection = quiz['macroNutrient'] ?? [];

  /** Because this cannot be represented by tags, this is simply to create the
   * illusion that selecting "I Prefer" matters  */
  const softSelectLs =
    localStorage.getItem(SODIUM_HARD_CONSTRAINT_OPT_OUT_LS_KEY) === 'true';

  const [softSelect, setSoftSelect] = useState(softSelectLs);

  const handleSetSoftSelect = (value: boolean) => {
    setSoftSelect(value);
    localStorage.setItem(
      SODIUM_HARD_CONSTRAINT_OPT_OUT_LS_KEY,
      value ? 'true' : 'false'
    );
  };

  const lowSodiumActive = macroSection.includes(tags.LowSodium);
  const onlyLowSodiumActive = macroSection.includes(tags.OnlyLowSodium);

  const getStatus = (): lowSodiumRestriction => {
    if (!lowSodiumActive) return 'none';
    if (onlyLowSodiumActive) return 'hard';
    if (lowSodiumActive && !softSelect) return 'unselectedSoft';
    return 'soft';
  };

  const onOptionSelect = (status: lowSodiumRestriction) => {
    if (status === 'hard') {
      onQuestionAnswer(quiz, 'macroNutrient', [
        ...(quiz.macroNutrient ?? []), // in reality, this has to include at least 26, but TS does not know that
        tags.OnlyLowSodium,
      ]);
      handleSetSoftSelect(false);

      segmentAnalytics.track('Quiz Button Clicked', {
        button_name: 'low_sodium_hard_constraint',
        path: '/quiz/macro-nutrient/low-sodium',
      });
    }

    if (status === 'soft') {
      //  user is opting out of the soft constraint
      handleSetSoftSelect(true);
      onQuestionAnswer(
        quiz,
        'macroNutrient',
        (quiz.macroNutrient ?? []).filter((tag) => tag !== tags.OnlyLowSodium)
      );

      segmentAnalytics.track('Quiz Button Clicked', {
        button_name: 'low_sodium_soft_constraint',
        path: '/quiz/macro-nutrient/low-sodium',
      });
    }
  };

  return { status: getStatus(), onOptionSelect };
};

interface Props {
  testid: string;
  scrollRef: RefObject<HTMLDivElement>;
}

const visibleTags = [
  tags.CarbConscious,
  tags.HighProtein,
  tags.LessSugar,
  tags.AntiInflamitory,
  tags.GutFriendly,
  tags.ImmunityBoosting,
  tags.LowSodium,
  tags.LowCholesterol,
  tags.LowFodmap,
  tags.LowFat,
  tags.CalorieConscious,
  tags.HighFiber,
] as const;

type VisibleTagId = (typeof visibleTags)[number];

const getIllustration = (tagId: VisibleTagId): JSX.Element => {
  switch (tagId) {
    case tags.CarbConscious:
      return <LowCarb />;
    case tags.HighProtein:
      return <HighProtein />;
    case tags.LessSugar:
      return <LowSugar />;
    case tags.AntiInflamitory:
      return <AntiInflamitory />;
    case tags.GutFriendly:
      return <GutHealth />;
    case tags.ImmunityBoosting:
      return <Immunity />;
    case tags.LowSodium:
      return <LowSodium />;
    case tags.LowCholesterol:
      return <LowCholesterol />;
    case tags.LowFodmap:
      return <LowFodmap />;
    case tags.LowFat:
      return <LowFat />;
    case tags.CalorieConscious:
      return <CalorieConscious />;
    case tags.HighFiber:
      return <HighFiber />;
  }
};

export function MacroNutrient(props: Props) {
  const { testid, scrollRef } = props;
  const [trackedItems, setTrackedItems] = useState<QuizPreferenceItem[]>([]);

  useScrollToTopFixIOS(scrollRef);

  const isDesktop = useMedia(BreakpointQueryLarge);

  const lowSodiumHook = useLowSodiumHardRestriction();

  return (
    <QuizQuestionScreen
      testid={testid}
      heroText="Do you have nutrition preferences?"
      subCopy="We’ll prioritize foods that meet your selections."
      sectionLabel={<SectionLabel label="About you" />}
      onSubmitAnalytics={() => {
        tagAnalytics('Quiz Nutritional Preferences', trackedItems);
      }}
    >
      <ControlTagSelect
        ariaLabel="Select your nutrition preferences"
        tagGroupId={tagGroups.nutrition}
        question="macroNutrient"
        layoutVariant={!isDesktop ? 'two-column' : 'one-column-to-two-column'}
        filterIds={[...visibleTags]}
        defaultValue={[]}
        onItemSelectionChange={[
          {
            itemValue: tags.LowSodium,
            handler: () => {
              segmentAnalytics.track('Quiz Button Clicked', {
                button_name: 'low_sodium_top_constraint',
              });
            },
          },
        ]}
        onTrackSelected={(items: QuizPreferenceItem[]) => {
          setTrackedItems(items);
        }}
      >
        {({ active, tag, index }) => {
          const { id: tagId, name: tagName, remark: remark } = tag;

          return (
            <Toggle
              active={active}
              testid={`${testid}-toggle-tag-${tagId}`}
              label={tagName}
              variant={ToggleVariant.Three}
              illustration={getIllustration(
                tagId as (typeof visibleTags)[number]
              )}
              subcopy={remark}
              style={{ animationDelay: `${0.05 * index + 0.5}s` }}
            />
          );
        }}
      </ControlTagSelect>

      <Route path={ROUTE_MACRO_NUTRIENT_LOW_SODIUM}>
        <LowSodiumOptions {...lowSodiumHook} />
      </Route>
    </QuizQuestionScreen>
  );
}

interface LowSodiumOptionsProps {
  status: lowSodiumRestriction;
  onOptionSelect: (status: lowSodiumRestriction) => void;
}
export const LowSodiumOptions = ({
  status,
  onOptionSelect,
}: LowSodiumOptionsProps) => {
  return (
    <section
      data-testid="low-sodium-restrictions"
      className={styles['low-sodium']}
      ref={(el) => {
        if (el === null) {
          return;
        }

        el.scrollIntoView({ behavior: 'smooth' });
      }}
    >
      <Heading variant="H3" className={styles['low-sodium-heading']}>
        How restrictive are your sodium needs?
      </Heading>
      <div className={styles['toggles']}>
        <span
          className={styles['toggle-wrapper']}
          onClick={() => onOptionSelect('hard')}
        >
          <Toggle
            className={styles['low-sodium-toggle']}
            testid="low-sodium-hard-option"
            active={status === 'hard'}
            illustration={null}
            variant={ToggleVariant.Three}
            label=" I only eat low sodium meals (<560mg)."
          />
        </span>
        <span
          className={styles['toggle-wrapper']}
          onClick={() => onOptionSelect('soft')}
        >
          <Toggle
            className={styles['low-sodium-toggle']}
            testid="low-sodium-soft-option"
            active={status === 'soft'}
            illustration={null}
            variant={ToggleVariant.Three}
            label=" I prefer low sodium when possible."
          />
        </span>
      </div>
    </section>
  );
};
